import { useState, useEffect } from 'react'
import { FiX, FiArrowLeft, FiUser, FiFileText, FiCreditCard } from 'react-icons/fi'
import api from '../utils/api'

const fmt = (v) => (v == null || isNaN(v)) ? '0,00' : Number(v).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
const fmtNum = (v) => (v == null || isNaN(v)) ? '0' : Number(v).toLocaleString('tr-TR', { maximumFractionDigits: 2 })

// initial: { type:'cari', blcari, unvan } | { type:'fatura', fatno } | { type:'tahsilat', blcari, evrak_no }
export default function AlberoDetayModal({ initial, onClose }) {
  const [stack, setStack] = useState([initial])
  const cur = stack[stack.length - 1]
  const push = (v) => setStack(s => [...s, v])
  const back = () => setStack(s => s.length > 1 ? s.slice(0, -1) : s)

  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [err, setErr] = useState(null)

  useEffect(() => {
    const h = (e) => { if (e.key === 'Escape') onClose && onClose() }
    window.addEventListener('keydown', h)
    return () => window.removeEventListener('keydown', h)
  }, [onClose])

  useEffect(() => {
    let alive = true
    setLoading(true); setErr(null); setData(null)
    const load = async () => {
      try {
        let path, params = {}
        if (cur.type === 'cari') { path = '/albero/cari-detay'; params = cur.blcari ? { blcari: cur.blcari } : { unvan: cur.unvan } }
        else if (cur.type === 'fatura') { path = '/albero/fatura-detay'; params = { fatno: cur.fatno } }
        else if (cur.type === 'tahsilat') { path = '/albero/tahsilat-detay'; params = { blcari: cur.blcari, evrak_no: cur.evrak_no } }
        const { data: r } = await api.get(path, { params })
        if (alive) setData(r)
      } catch (e) {
        if (alive) setErr(e.response?.data?.detail || 'Detay yüklenemedi')
      }
      if (alive) setLoading(false)
    }
    load()
    return () => { alive = false }
  }, [stack.length, cur.type, cur.blcari, cur.fatno, cur.evrak_no, cur.unvan])

  const title = cur.type === 'cari' ? (data?.kunye?.UNVAN || cur.unvan || 'Cari Detay')
    : cur.type === 'fatura' ? `Fatura #${cur.fatno}`
    : `Tahsilat — ${data?.cari || ''}`
  const Icon = cur.type === 'cari' ? FiUser : cur.type === 'fatura' ? FiFileText : FiCreditCard

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-3" onClick={onClose}>
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-3xl flex flex-col"
        style={{ maxHeight: '88vh' }} onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center gap-2 px-4 py-3 border-b dark:border-gray-700 bg-slate-700 text-white rounded-t-xl">
          {stack.length > 1 && (
            <button onClick={back} className="p-1 hover:bg-white/20 rounded" title="Geri"><FiArrowLeft size={16} /></button>
          )}
          <Icon size={16} className="text-white/80" />
          <div className="flex-1 font-semibold text-sm truncate">{title}</div>
          <button onClick={onClose} className="p-1 hover:bg-white/20 rounded"><FiX size={18} /></button>
        </div>

        {/* Body */}
        <div className="overflow-auto p-4" style={{ overscrollBehavior: 'contain' }}>
          {loading && <div className="text-center text-gray-400 py-10">Yükleniyor...</div>}
          {err && <div className="text-center text-red-500 py-10">{err}</div>}

          {!loading && !err && cur.type === 'cari' && data && (
            <CariView data={data} onFatura={(fatno) => push({ type: 'fatura', fatno })}
              onTahsilat={(evrak_no) => push({ type: 'tahsilat', blcari: data.blcari, evrak_no })} />
          )}
          {!loading && !err && cur.type === 'fatura' && data && (
            <FaturaView data={data} onCari={(blcari) => push({ type: 'cari', blcari })} />
          )}
          {!loading && !err && cur.type === 'tahsilat' && data && (
            <TahsilatView data={data} />
          )}
        </div>
      </div>
    </div>
  )
}

const tahsilatTip = (t) => ['tahsilat', 'posgelir', 'gelenhav', 'nakit'].includes(String(t || '').toLowerCase())

function CariView({ data, onFatura, onTahsilat }) {
  const k = data.kunye || {}
  const bak = data.bakiye || 0
  return (
    <div>
      {/* Künye */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mb-4 text-xs">
        <Info label="Vergi/TC No" value={k.VERGINO || k.TCKIMLIK} />
        <Info label="Telefon" value={k.GSM || k.TELEFON} />
        <Info label="İl / İlçe" value={[k.IL, k.ILCE].filter(Boolean).join(' / ')} />
        <div className="col-span-2 md:col-span-3">
          <div className="text-[10px] uppercase text-gray-400">Adres</div>
          <div className="text-gray-700 dark:text-gray-200">{k.ADRES || '-'}</div>
        </div>
      </div>
      <div className={`inline-block rounded-lg px-4 py-2 mb-4 font-bold ${bak > 0 ? 'bg-red-50 text-red-700 dark:bg-red-900/30 dark:text-red-300' : 'bg-green-50 text-green-700 dark:bg-green-900/30 dark:text-green-300'}`}>
        Bakiye: {fmt(bak)} TL {bak > 0 ? '(Borçlu)' : bak < 0 ? '(Alacaklı)' : ''}
      </div>

      <div className="text-[11px] font-semibold text-gray-500 mb-1">Son Hareketler ({(data.hareketler || []).length})</div>
      <div className="overflow-auto border rounded dark:border-gray-700" style={{ maxHeight: '45vh' }}>
        <table className="w-full text-[11px]">
          <thead className="sticky top-0 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-200">
            <tr>
              <th className="text-left px-2 py-1">Tarih</th>
              <th className="text-left px-2 py-1">İşlem</th>
              <th className="text-left px-2 py-1">Evrak</th>
              <th className="text-right px-2 py-1">Borç</th>
              <th className="text-right px-2 py-1">Alacak</th>
              <th className="text-left px-2 py-1">Açıklama</th>
            </tr>
          </thead>
          <tbody>
            {(data.hareketler || []).map((h, i) => {
              const clickable = tahsilatTip(h.TIP)
              return (
                <tr key={i} className={`border-b border-gray-50 dark:border-gray-700/50 ${clickable ? 'cursor-pointer hover:bg-blue-50 dark:hover:bg-blue-900/20' : ''}`}
                  onClick={clickable ? () => onTahsilat(h.EVRAK_NO) : undefined}>
                  <td className="px-2 py-1 whitespace-nowrap">{h.TARIH}</td>
                  <td className={`px-2 py-1 font-medium ${clickable ? 'text-green-600' : 'text-gray-600 dark:text-gray-300'}`}>{h.TIP}</td>
                  <td className="px-2 py-1 text-gray-500">{h.EVRAK_NO}</td>
                  <td className="text-right px-2 py-1">{h.BORC ? fmt(h.BORC) : ''}</td>
                  <td className="text-right px-2 py-1 text-green-700 dark:text-green-400">{h.ALACAK ? fmt(h.ALACAK) : ''}</td>
                  <td className="px-2 py-1 text-gray-400 truncate max-w-[220px]">{h.ACIKLAMA}</td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </div>
  )
}

function FaturaView({ data, onCari }) {
  const k = data.kunye || {}
  return (
    <div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-4 text-xs">
        <Info label="Tarih" value={k.TARIH} />
        <div>
          <div className="text-[10px] uppercase text-gray-400">Cari</div>
          <button onClick={() => k.BLCARI && onCari(k.BLCARI)}
            className="text-left text-blue-600 dark:text-blue-400 hover:underline font-medium">{k.CARI}</button>
        </div>
        <Info label="Evrak No" value={k.EVRAK_NO} />
        <Info label="E-Fatura No" value={k.EF_NO || '— (gönderilmemiş)'} />
      </div>

      <div className="overflow-auto border rounded dark:border-gray-700" style={{ maxHeight: '50vh' }}>
        <table className="w-full text-[11px]">
          <thead className="sticky top-0 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-200">
            <tr>
              <th className="text-left px-2 py-1">Ürün</th>
              <th className="text-right px-2 py-1">Miktar</th>
              <th className="text-left px-2 py-1">Br.</th>
              <th className="text-right px-2 py-1">Br. Fiyat</th>
              <th className="text-right px-2 py-1">Tutar</th>
            </tr>
          </thead>
          <tbody>
            {(data.kalemler || []).map((r, i) => (
              <tr key={i} className="border-b border-gray-50 dark:border-gray-700/50">
                <td className="px-2 py-1">{r.URUN}</td>
                <td className="text-right px-2 py-1">{fmtNum(r.ADET)}</td>
                <td className="px-2 py-1 text-gray-500">{r.BIRIM}</td>
                <td className="text-right px-2 py-1">{fmt(r.BRMFIYAT)}</td>
                <td className="text-right px-2 py-1 font-medium">{fmt(r.TOPLAM)}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="bg-slate-700 text-white font-bold">
              <td colSpan={4} className="px-2 py-1.5 text-right">GENEL TOPLAM</td>
              <td className="text-right px-2 py-1.5">{fmt(k.TOPLAM)}</td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  )
}

function TahsilatView({ data }) {
  const rows = data.hareketler || []
  const toplam = rows.reduce((s, r) => s + (r.TUTAR || 0), 0)
  return (
    <div>
      <div className="overflow-auto border rounded dark:border-gray-700" style={{ maxHeight: '55vh' }}>
        <table className="w-full text-[11px]">
          <thead className="sticky top-0 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-200">
            <tr>
              <th className="text-left px-2 py-1">Tarih</th>
              <th className="text-left px-2 py-1">Tür</th>
              <th className="text-left px-2 py-1">Evrak</th>
              <th className="text-right px-2 py-1">Tutar</th>
              <th className="text-left px-2 py-1">Açıklama</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i} className="border-b border-gray-50 dark:border-gray-700/50">
                <td className="px-2 py-1 whitespace-nowrap">{r.TARIH}</td>
                <td className="px-2 py-1 font-medium text-green-600">{r.TIP}</td>
                <td className="px-2 py-1 text-gray-500">{r.EVRAK_NO}</td>
                <td className="text-right px-2 py-1 text-green-700 dark:text-green-400 font-medium">{fmt(r.TUTAR)}</td>
                <td className="px-2 py-1 text-gray-400 truncate max-w-[240px]">{r.ACIKLAMA}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="bg-slate-700 text-white font-bold">
              <td colSpan={3} className="px-2 py-1.5 text-right">TOPLAM</td>
              <td className="text-right px-2 py-1.5">{fmt(toplam)}</td>
              <td></td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  )
}

function Info({ label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase text-gray-400">{label}</div>
      <div className="text-gray-700 dark:text-gray-200">{value || '-'}</div>
    </div>
  )
}
